class CartGiftingOptions {
	constructor(host, options) {
		this.host = host;
		this.giftingSource = options.giftingSource;
		this.getSectionIdsForRequest = options.getSectionIdsForRequest;
		this.applyCartSections = options.applyCartSections;
		this.loadingHost = options.loadingHost || host;
		this._giftMessageTimers = {};
		this._giftMessagePersistPromises = {};
	}

	handleClick(event) {
		const giftMain = event.target.closest("[data-gift-product-main]");
		if (giftMain) {
			if (event.target.closest("label.cart-drawer__gifting-checkbox")) return false;
			const checkbox = giftMain
				.closest(".cart-drawer__gifting-item")
				?.querySelector("[data-gift-product]");
			if (!checkbox) return false;
			event.preventDefault();
			checkbox.click();
			return true;
		}

		const variantButton = event.target.closest("[data-gift-variant]");
		if (variantButton) {
			event.preventDefault();
			this.onVariantButtonClick(variantButton);
			return true;
		}
		return false;
	}

	handleKeyDown(event) {
		const giftMain = event.target.closest("[data-gift-product-main]");
		if (!giftMain) return;

		const code = event.code.toUpperCase();
		if (code !== "SPACE" && code !== "ENTER") return;

		const checkbox = giftMain
			.closest(".cart-drawer__gifting-item")
			?.querySelector("[data-gift-product]");
		if (!checkbox) return;

		event.preventDefault();
		checkbox.click();
	}

	handleGiftMessageInput(event) {
		const textarea = event.target.closest("[data-gift-message]");
		if (!textarea || !this.host.contains(textarea)) return;

		this.updateGiftMessageCount(textarea);

		const handle = textarea.dataset.productHandle;
		if (!handle) return;

		clearTimeout(this._giftMessageTimers[handle]);
		this._giftMessageTimers[handle] = setTimeout(() => {
			delete this._giftMessageTimers[handle];
			this.persistGiftMessage(textarea);
		}, GIFT_MESSAGE_DEBOUNCE_MS);
	}

	handleChange(event) {
		const giftMessageTextarea = event.target.closest("[data-gift-message]");
		if (giftMessageTextarea && this.host.contains(giftMessageTextarea)) {
			const handle = giftMessageTextarea.dataset.productHandle;
			if (handle) {
				clearTimeout(this._giftMessageTimers[handle]);
				delete this._giftMessageTimers[handle];
			}
			this.persistGiftMessage(giftMessageTextarea);
			return;
		}

		const giftProductCheckbox = event.target.closest("[data-gift-product]");
		if (!giftProductCheckbox) return;

		const productHandle = giftProductCheckbox.dataset.productHandle;
		const variantId = giftProductCheckbox.dataset.variantId;

		if (variantId) {
			if (giftProductCheckbox.checked) {
				this.addGiftVariant({ variantId, productHandle });
			} else {
				this.removeGiftByVariantId({ variantId });
			}
			return;
		}

		const variantsPanel = giftProductCheckbox
			.closest(".cart-drawer__gifting-item")
			?.querySelector(
				'[data-gift-variants][data-product-handle="' + productHandle + '"]'
			);

		if (giftProductCheckbox.checked) {
			this.setGiftVariantsPanelOpen(variantsPanel, true);
			return;
		}

		this.setGiftVariantsPanelOpen(variantsPanel, false);
		this.removeAllGiftsForProductHandle({ productHandle });
	}

	setGiftingLoading(isLoading) {
		this.loadingHost.classList.toggle("gifting-loading", Boolean(isLoading));

		const panel = this.host.querySelector(".cart-drawer__panel--gifting");
		if (!panel) return;

		panel
			.querySelectorAll("input, button, textarea")
			.forEach((el) => el.toggleAttribute("disabled", Boolean(isLoading)));
	}

	async onVariantButtonClick(button) {
		const variantId = button.dataset.variantId;
		const productHandle = button.dataset.productHandle;
		const isActive = button.classList.contains("is-active");

		if (!variantId || !productHandle) return;

		if (isActive) {
			await this.removeGiftByVariantId({ variantId });
			return;
		}

		await this.removeAllGiftsForProductHandle({ productHandle });
		await this.addGiftVariant({ variantId, productHandle });
	}

	_applyCartResponse(parsedState) {
		if ("suppressNotificationOnce" in this.host) {
			this.host.suppressNotificationOnce = true;
		}
		this.applyCartSections(parsedState);
	}

	async addGiftVariant({ variantId, productHandle }) {
		try {
			this.setGiftingLoading(true);

			const config = fetchConfig("javascript");
			config.headers["X-Requested-With"] = "XMLHttpRequest";
			delete config.headers["Content-Type"];

			const formData = new FormData();
			formData.append("id", variantId);
			formData.append("quantity", 1);
			formData.append("properties[_gifting_option]", "true");
			formData.append("properties[_gifting_source]", this.giftingSource);
			if (productHandle)
				formData.append("properties[_gifting_product_handle]", productHandle);

			const giftMsg = this.getGiftMessageValue(productHandle);
			if (giftMsg) formData.append("properties[Gift message]", giftMsg);

			formData.append("sections", this.getSectionIdsForRequest());
			formData.append("sections_url", window.location.pathname);
			if (this.host.setActiveElement) {
				this.host.setActiveElement(document.activeElement);
			}

			config.body = formData;

			const response = await fetch(`${routes.cart_add_url}`, config);
			const data = await response.json();

			if (data?.status) {
				document.dispatchEvent(
					new CustomEvent("cart:error", {
						detail: {
							source:
								this.giftingSource === "cart_page"
									? "cart-page-gifting"
									: "cart-drawer-gifting",
							productVariantId: variantId,
							errors: data.description,
							message: data.message,
						},
					})
				);
				return;
			}

			this._applyCartResponse(data);
		} catch (e) {
			console.error(e);
		} finally {
			this.setGiftingLoading(false);
		}
	}

	getGiftMessageValue(productHandle) {
		if (!productHandle) return "";
		const ta = this.host.querySelector(
			'[data-gift-message][data-product-handle="' + productHandle + '"]'
		);
		if (!ta) return "";
		return ta.value.trim().slice(0, 120);
	}

	async persistGiftMessage(textarea) {
		const productHandle = textarea?.dataset?.productHandle;
		if (!productHandle) return;

		// Chain onto any in-flight save for this handle so overlapping calls
		// (debounce fire + blur) can't race each other against cart/change.js.
		const previous =
			this._giftMessagePersistPromises[productHandle] || Promise.resolve();
		const run = previous
			.catch(() => {})
			.then(() => this._persistGiftMessageNow(textarea, productHandle));

		this._giftMessagePersistPromises[productHandle] = run;
		try {
			await run;
		} finally {
			if (this._giftMessagePersistPromises[productHandle] === run) {
				delete this._giftMessagePersistPromises[productHandle];
			}
		}
	}

	hasPendingGiftMessage() {
		return Boolean(
			Object.keys(this._giftMessageTimers).length ||
				Object.keys(this._giftMessagePersistPromises).length
		);
	}

	async flushPendingGiftMessages() {
		const handles = new Set([
			...Object.keys(this._giftMessageTimers),
			...Object.keys(this._giftMessagePersistPromises),
		]);
		if (!handles.size) return;

		await Promise.all(
			Array.from(handles).map((handle) => {
				clearTimeout(this._giftMessageTimers[handle]);
				delete this._giftMessageTimers[handle];

				if (this._giftMessagePersistPromises[handle]) {
					return this._giftMessagePersistPromises[handle];
				}

				const textarea = this.host.querySelector(
					'[data-gift-message][data-product-handle="' + handle + '"]'
				);
				return textarea ? this.persistGiftMessage(textarea) : Promise.resolve();
			})
		);
	}

	async _persistGiftMessageNow(textarea, productHandle) {
		const msg = textarea.value.trim().slice(0, 120);

		let cart;
		try {
			cart = await this.fetchCartState();
		} catch (e) {
			console.error(e);
			return;
		}

		const lines = this.findGiftLinesByProductHandle(cart, productHandle);
		if (!lines.length) return;

		const line = lines[0];
		const item = cart.items[line - 1];
		if (!item) return;

		const hasLegacyKey = Boolean(item.properties && item.properties._gift_message);
		const prevMsg = String(
			(item.properties &&
				(item.properties["Gift message"] ?? item.properties._gift_message)) ||
				""
		).trim();
		if (prevMsg === msg && !hasLegacyKey) return;

		try {
			this.setGiftingLoading(true);

			// cart/change.js merges properties, so clear by empty string (delete is a no-op).
			const properties = { ...(item.properties || {}) };
			properties["Gift message"] = msg;
			if (hasLegacyKey) properties._gift_message = "";

			const body = JSON.stringify({
				line,
				quantity: item.quantity,
				properties,
				sections: this.getSectionIdsForRequest(),
				sections_url: window.location.pathname,
			});

			const res = await fetch(`${routes.cart_change_url}`, {
				...fetchConfig(),
				...{ body },
			});
			const text = await res.text();
			const parsedState = JSON.parse(text);

			if (parsedState?.errors) return;
			this._applyCartResponse(parsedState);
		} catch (e) {
			console.error(e);
		} finally {
			this.setGiftingLoading(false);
		}
	}

	async removeGiftByVariantId({ variantId }) {
		try {
			this.setGiftingLoading(true);

			const cart = await this.fetchCartState();
			const line = this.findGiftLineByVariantId(cart, Number(variantId));
			if (!line) return;

			await this.changeLineQuantity({ line, quantity: 0 });
		} catch (e) {
			console.error(e);
		} finally {
			this.setGiftingLoading(false);
		}
	}

	async removeAllGiftsForProductHandle({ productHandle }) {
		if (!productHandle) return;

		try {
			this.setGiftingLoading(true);
			const cart = await this.fetchCartState();
			const lines = this.findGiftLinesByProductHandle(cart, productHandle);

			for (const line of lines.sort((a, b) => b - a)) {
				await this.changeLineQuantity({ line, quantity: 0 });
			}
		} catch (e) {
			console.error(e);
		} finally {
			this.setGiftingLoading(false);
		}
	}

	async changeLineQuantity({ line, quantity }) {
		const body = JSON.stringify({
			line,
			quantity,
			sections: this.getSectionIdsForRequest(),
			sections_url: window.location.pathname,
		});

		const res = await fetch(`${routes.cart_change_url}`, {
			...fetchConfig(),
			...{ body },
		});
		const text = await res.text();
		const parsedState = JSON.parse(text);

		if (parsedState?.errors) return;
		this._applyCartResponse(parsedState);
	}

	async fetchCartState() {
		const res = await fetch(`${routes.cart_url}.js`, { credentials: "same-origin" });
		return await res.json();
	}

	findGiftLineByVariantId(cart, variantId) {
		if (!cart?.items?.length) return null;
		const index = cart.items.findIndex(
			(item) =>
				Number(item.variant_id) === Number(variantId) &&
				lineItemIsGiftingAddon(item)
		);
		return index === -1 ? null : index + 1;
	}

	findGiftLinesByProductHandle(cart, productHandle) {
		if (!cart?.items?.length) return [];
		const lines = [];
		cart.items.forEach((item, idx) => {
			if (
				lineItemIsGiftingAddon(item) &&
				item.properties &&
				item.properties._gifting_product_handle === productHandle
			) {
				lines.push(idx + 1);
			}
		});
		return lines;
	}

	setGiftVariantsPanelOpen(panel, open) {
		if (!panel) return;
		const inner = panel.querySelector(".cart-drawer__gifting-variants-inner");
		if (open) {
			panel.classList.add("is-open");
			panel.setAttribute("aria-hidden", "false");
			inner?.removeAttribute("inert");
		} else {
			panel.classList.remove("is-open");
			panel.setAttribute("aria-hidden", "true");
			inner?.setAttribute("inert", "");
		}
	}

	syncGiftingPanels() {
		this.host.querySelectorAll("[data-gift-product]").forEach((checkbox) => {
			const productHandle = checkbox.dataset.productHandle;
			if (!productHandle) return;

			const variantsPanel = this.host.querySelector(
				'[data-gift-variants][data-product-handle="' + productHandle + '"]'
			);
			const messagePanel = this.host.querySelector(
				'[data-gift-message-panel][data-product-handle="' + productHandle + '"]'
			);

			if (checkbox.dataset.variantId) {
				if (messagePanel) {
					if (checkbox.checked) messagePanel.removeAttribute("hidden");
					else messagePanel.setAttribute("hidden", "");
				}
				return;
			}

			if (variantsPanel) {
				this.setGiftVariantsPanelOpen(variantsPanel, checkbox.checked);
			}
		});

		this.host.querySelectorAll("[data-gift-message]").forEach((textarea) => {
			this.updateGiftMessageCount(textarea);
		});
	}

	updateGiftMessageCount(textarea) {
		const wrap = textarea.closest(".cart-drawer__gifting-message-input-wrap");
		const countEl = wrap?.querySelector("[data-gift-message-count]");
		if (!countEl) return;
		const max = Number(textarea.getAttribute("maxlength")) || 120;
		countEl.textContent = `${textarea.value.length}/${max}`;
	}
}

class GiftingOptionsDrawer extends HTMLElement {
	constructor() {
		super();
		this._openTrigger = null;
		this._onFooterClick = this._handleFooterClick.bind(this);
		this._onDrawerClick = this._handleDrawerClick.bind(this);
		this._onKeyup = this._handleKeyup.bind(this);
	}

	connectedCallback() {
		if (this._gifting) return;

		this._gifting = new CartGiftingOptions(this, {
			giftingSource: "cart_page",
			getSectionIdsForRequest: () => {
				const el = document.querySelector("cart-items");
				return el ? el.getSectionIdsForRequest() : [];
			},
			applyCartSections: (parsedState) => {
				const visibleCount = cartVisibleItemCount(parsedState);
				const ci = document.querySelector("cart-items");
				if (ci) {
					ci.applySectionReplacements(parsedState);
					ci.classList.toggle("is-empty", visibleCount === 0);
				}
				const cartFooter = document.getElementById("main-cart-footer");
				if (cartFooter) {
					cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
				}
				const cartDrawerWrapper = document.querySelector("cart-drawer");
				if (cartDrawerWrapper) {
					cartDrawerWrapper.classList.toggle(
						"is-empty",
						visibleCount === 0
					);
				}
				this._gifting.syncGiftingPanels();
			},
			loadingHost: this,
		});

		this.addEventListener("click", this._onDrawerClick);
		this.addEventListener("keydown", (e) => this._gifting.handleKeyDown(e));
		this.addEventListener("input", (e) => this._gifting.handleGiftMessageInput(e));
		this.addEventListener("change", (e) => this._gifting.handleChange(e));
		this.addEventListener("click", (e) => this._gifting.handleClick(e));

		this.addEventListener("keyup", this._onKeyup);

		this.querySelector("#GiftingOptionsDrawer-Overlay")?.addEventListener(
			"click",
			() => this.close()
		);

		document
			.getElementById("main-cart-footer")
			?.addEventListener("click", this._onFooterClick);

		this._gifting.syncGiftingPanels();
	}

	disconnectedCallback() {
		document
			.getElementById("main-cart-footer")
			?.removeEventListener("click", this._onFooterClick);
		if (this.classList.contains("active")) {
			document.body.classList.remove("cart-overflow-hidden");
			document.body.classList.remove("overflow-hidden");
			document.body.classList.remove("overflow-clip");
		}
	}

	_handleFooterClick(event) {
		if (!event.target.closest("[data-gifting-page-open]")) return;
		event.preventDefault();
		this._openTrigger =
			event.target.closest("[data-gifting-page-open]") || document.body;
		this.open();
	}

	_handleDrawerClick(event) {
		if (event.target.closest("[data-gifting-page-close]")) {
			event.preventDefault();
			this.close();
		}
	}

	_handleKeyup(event) {
		if (event.code === "Escape" && this.classList.contains("active")) {
			this.close();
		}
	}

	open() {
		setTimeout(() => {
			this.classList.add("animate", "active");
		});

		this.addEventListener(
			"transitionend",
			() => {
				const inner = this.querySelector(".drawer__inner");
				trapFocus(this, inner || this);
			},
			{ once: true }
		);

		document.body.classList.add("cart-overflow-hidden");
		if (!isSafari()) {
			document.body.classList.add("overflow-hidden");
		} else {
			document.body.classList.add("overflow-clip");
		}
	}

	close() {
		this.classList.remove("active");
		removeTrapFocus(this._openTrigger);
		document.body.classList.remove("cart-overflow-hidden");
		if (!isSafari()) {
			document.body.classList.remove("overflow-hidden");
		} else {
			document.body.classList.remove("overflow-clip");
		}
	}
}

if (!customElements.get("gifting-options-drawer")) {
	customElements.define("gifting-options-drawer", GiftingOptionsDrawer);
}

// Checkout buttons submit their <form> as soon as they're clicked, which can
// race the gift-message autosave (debounced on typing, immediate on blur).
// global.js also listens for this same click (bubble phase, see "CHECKOUT
// BUTTON LOADING STATE") and resubmits the form itself a couple of frames
// later regardless of anything async here, so this must run in the capture
// phase to get first refusal on the event and stop it from reaching that
// handler until the gift message is safely saved.
function bindGiftingCheckoutFlush(checkoutSelector, getGiftingOptions) {
	document.addEventListener(
		"click",
		(event) => {
			const button = event.target.closest(checkoutSelector);
			if (!button || button.dataset.giftingFlushing === "true") return;

			const gifting = getGiftingOptions();
			if (!gifting || !gifting.hasPendingGiftMessage()) return;

			event.preventDefault();
			event.stopImmediatePropagation();

			button.dataset.giftingFlushing = "true";
			button.setAttribute("aria-disabled", "true");
			button.classList.add("loading");
			button.querySelector(".loading-overlay__spinner")?.classList.remove("hidden");

			gifting.flushPendingGiftMessages().finally(() => {
				delete button.dataset.giftingFlushing;
				button.removeAttribute("aria-disabled");
				button.classList.remove("loading");
				button.querySelector(".loading-overlay__spinner")?.classList.add("hidden");
				// Re-dispatch: hasPendingGiftMessage() is now false, so this
				// pass falls through to global.js's own loading state + submit.
				button.click();
			});
		},
		true
	);
}

bindGiftingCheckoutFlush(
	"#CartDrawer-Checkout",
	() => document.querySelector("cart-drawer")?._gifting
);
bindGiftingCheckoutFlush(
	'button[name="checkout"][form="cart"]',
	() => document.querySelector("gifting-options-drawer")?._gifting
);
